<template>
    <!-- Async search mode: input + custom dropdown -->
    <div v-if="isAsyncMode" class="relative" ref="fetchContainerRef">
        <input
            ref="searchInputRef"
            v-model="searchQuery"
            type="text"
            class="relative w-full rounded-lg bg-white py-2.5 pl-3 pr-10 rtl:pl-10 rtl:pr-3 text-start text-sm border border-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
            :class="{ 'border-red-500 focus:ring-red-500/20 focus:border-red-500': hasError }"
            :placeholder="selectedOption ? selectedOption[textAttribute] : (placeholder || $t('common.components.select_an_option'))"
            style="color: #0f172a"
            autocomplete="off"
            @focus="openAndFetch"
            @input="onSearchInput"
        />
        <span class="pointer-events-none absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 flex items-center pr-2 rtl:pr-0 rtl:pl-2">
            <svg v-if="loading" class="h-5 w-5 text-slate-400 animate-spin" viewBox="0 0 24 24" fill="none"><circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle><path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path></svg>
            <svg v-else class="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.75 7.25a.75.75 0 011.1 0L10 15.148l2.7-2.909a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 010-1.02z" clip-rule="evenodd" /></svg>
        </span>
        <ul
            v-if="showFetchDropdown"
            :class="['t-rich-select-dropdown absolute z-[9999] max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-xl ring-1 ring-slate-200 focus:outline-none', dropdownPositionClass]"
            style="color: #0f172a"
        >
            <li v-if="safeOptions.length === 0 && !loading" class="px-3 py-2 text-sm text-center" style="color: #475569">{{ $t('common.components.type_chars_to_search', { n: props.minimumInputLength }) }}</li>
            <li v-else-if="safeOptions.length === 0" class="px-3 py-2 text-sm text-center" style="color: #475569">{{ $t('common.components.no_options') }}</li>
            <li
                v-for="option in safeOptions"
                :key="option[valueAttribute]"
                role="option"
                class="relative cursor-pointer select-none py-2.5 pl-10 pr-4 rtl:pl-4 rtl:pr-10 transition-colors hover:bg-blue-50"
                :class="{ 'bg-blue-50': selectedOption && option[valueAttribute] === selectedOption[valueAttribute] }"
                @mousedown.prevent="selectFetchOption(option)"
            >
                <slot name="label" :option="option" :selected="selectedOption && option[valueAttribute] === selectedOption[valueAttribute]" :active="false">
                    <span class="block truncate">{{ option[textAttribute] }}</span>
                </slot>
                <span v-if="selectedOption && option[valueAttribute] === selectedOption[valueAttribute]" class="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pl-3 rtl:pl-0 rtl:pr-3 text-blue-600">
                    <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" /></svg>
                </span>
            </li>
        </ul>
    </div>
    <!-- Static options mode: Listbox -->
    <Listbox v-else as="div" v-model="selectedOption">
        <div class="relative" ref="listboxTriggerRef">
            <ListboxButton
                @click="computePlacementIfAuto"
                class="relative w-full cursor-pointer rounded-lg bg-white py-2.5 pl-3 pr-10 rtl:pl-10 rtl:pr-3 text-start text-sm border border-slate-300 shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-colors"
                :class="{ 'border-red-500 focus:ring-red-500/20 focus:border-red-500': hasError }"
                style="color: #0f172a"
            >
                <span class="block truncate" :style="selectedOption ? { color: '#0f172a' } : { color: '#64748b' }">
                    {{ selectedOption ? selectedOption[textAttribute] : (placeholder || $t('common.components.select_an_option')) }}
                </span>
                <span class="pointer-events-none absolute inset-y-0 right-0 rtl:right-auto rtl:left-0 flex items-center pr-2 rtl:pr-0 rtl:pl-2">
                    <svg class="h-5 w-5 text-slate-400" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M10 3a.75.75 0 01.55.24l3.25 3.5a.75.75 0 11-1.1 1.02L10 4.852 7.3 7.76a.75.75 0 01-1.1-1.02l3.25-3.5A.75.75 0 0110 3zm-3.75 7.25a.75.75 0 011.1 0L10 15.148l2.7-2.909a.75.75 0 111.1 1.02l-3.25 3.5a.75.75 0 01-1.1 0l-3.25-3.5a.75.75 0 010-1.02z" clip-rule="evenodd" /></svg>
                </span>
            </ListboxButton>
            <transition leave-active-class="transition duration-100 ease-in" leave-from-class="opacity-100" leave-to-class="opacity-0">
                <ListboxOptions :class="['t-rich-select-dropdown absolute z-[9999] max-h-60 w-full overflow-auto rounded-lg bg-white py-1 text-sm shadow-xl ring-1 ring-slate-200 focus:outline-none', dropdownPositionClass]" style="color: #0f172a">
                    <div v-if="safeOptions.length === 0" class="px-3 py-2 text-sm text-center" style="color: #475569">{{ $t('common.components.no_options_available') }}</div>
                    <ListboxOption v-for="option in safeOptions" :key="option[valueAttribute]" :value="option" as="template" v-slot="{ active, selected }">
                        <li class="relative cursor-pointer select-none py-2.5 pl-10 pr-4 rtl:pl-4 rtl:pr-10 transition-colors" :class="[ active ? 'bg-blue-50' : '', selected ? 'bg-blue-50' : '' ]">
                            <div class="t-rich-select-option-inner" style="color: #111827 !important;">
                                <slot name="label" :option="option" :selected="selected" :active="active">
                                    <span class="block truncate">{{ option[textAttribute] }}</span>
                                </slot>
                            </div>
                            <span v-if="selected" class="absolute inset-y-0 left-0 rtl:left-auto rtl:right-0 flex items-center pl-3 rtl:pl-0 rtl:pr-3 text-blue-600">
                                <svg class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor"><path fill-rule="evenodd" d="M16.704 4.153a.75.75 0 01.143 1.052l-8 10.5a.75.75 0 01-1.127.075l-4.5-4.5a.75.75 0 011.06-1.06l3.894 3.893 7.48-9.817a.75.75 0 011.05-.143z" clip-rule="evenodd" /></svg>
                            </span>
                        </li>
                    </ListboxOption>
                </ListboxOptions>
            </transition>
        </div>
    </Listbox>
</template>

<script setup>
import { ref, watch, computed, onMounted, onUnmounted } from 'vue';
import { Listbox, ListboxButton, ListboxOptions, ListboxOption } from '@headlessui/vue';
import debounce from 'lodash/debounce';

const props = defineProps({
    options: {
        type: Array,
        default: () => [],
    },
    /** When provided, options are loaded by calling this function with search query. Must return Promise<{ results: Array }>. */
    fetchOptions: {
        type: Function,
        default: null,
    },
    /** Minimum characters before calling fetchOptions (only when fetchOptions is set). */
    minimumInputLength: {
        type: Number,
        default: 2,
    },
    modelValue: {
        type: [String, Number, Object],
        default: null,
    },
    placeholder: {
        type: String,
        default: '',
    },
    valueAttribute: {
        type: String,
        default: 'id',
    },
    textAttribute: {
        type: String,
        default: 'name',
    },
    hasError: {
        type: Boolean,
        default: false,
    },
    disabled: {
        type: Boolean,
        default: false,
    },
    /** 'top' = always open upward, 'bottom' = always downward, 'auto' = choose by available space below. */
    placement: {
        type: String,
        default: 'auto',
        validator: (v) => ['top', 'bottom', 'auto'].includes(v),
    },
});

const emit = defineEmits(['update:modelValue']);

/** When parent passes a function, use async search mode (input + fetch). */
const isAsyncMode = computed(() => typeof props.fetchOptions === 'function');

/** For placement="auto": computed on open from viewport space below. */
const effectivePlacement = ref('bottom');
const DROPDOWN_MIN_HEIGHT = 260;

function computePlacementIfAuto() {
    if (props.placement !== 'auto') return;
    const el = isAsyncMode.value ? fetchContainerRef.value : listboxTriggerRef.value;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const spaceBelow = typeof window !== 'undefined' ? window.innerHeight - rect.bottom : 0;
    effectivePlacement.value = spaceBelow < DROPDOWN_MIN_HEIGHT ? 'top' : 'bottom';
}

const placementToUse = computed(() =>
    props.placement === 'auto' ? effectivePlacement.value : props.placement
);
const dropdownPositionClass = computed(() =>
    placementToUse.value === 'top' ? 'bottom-full mb-1' : 'mt-1'
);

const searchQuery = ref('');
const fetchedOptions = ref([]);
const loading = ref(false);
const searchInputRef = ref(null);
const fetchContainerRef = ref(null);
const listboxTriggerRef = ref(null);
const fetchDropdownOpen = ref(false);

const doFetch = debounce(async () => {
    const q = (searchQuery.value || '').trim();
    if (!props.fetchOptions || q.length < props.minimumInputLength) {
        fetchedOptions.value = [];
        return;
    }
    loading.value = true;
    try {
        const result = await Promise.resolve(props.fetchOptions(q));
        fetchedOptions.value = Array.isArray(result?.results) ? result.results : [];
    } catch {
        fetchedOptions.value = [];
    } finally {
        loading.value = false;
    }
}, 300);

function openAndFetch() {
    computePlacementIfAuto();
    fetchDropdownOpen.value = true;
    if (props.fetchOptions && (searchQuery.value || '').trim().length >= props.minimumInputLength) {
        doFetch();
    } else {
        fetchedOptions.value = [];
    }
}

const showFetchDropdown = computed(() => fetchDropdownOpen.value);

function selectFetchOption(option) {
    selectedOption.value = option;
    searchQuery.value = option[props.textAttribute] || '';
    fetchDropdownOpen.value = false;
}

function onDocumentClick(e) {
    if (fetchContainerRef.value && !fetchContainerRef.value.contains(e.target)) {
        fetchDropdownOpen.value = false;
    }
}

function onSearchInput() {
    doFetch();
}

function onSelect(value) {
    if (props.fetchOptions && value) {
        searchQuery.value = value[props.textAttribute] || '';
    }
}

// When using fetchOptions, options come from fetchedOptions; otherwise from props.options
const safeOptions = computed(() => {
    if (props.fetchOptions) {
        return Array.isArray(fetchedOptions.value) ? fetchedOptions.value : [];
    }
    return Array.isArray(props.options) ? props.options : [];
});

const selectedOption = ref(
    safeOptions.value.find(option => option[props.valueAttribute] === props.modelValue) || null
);

watch(selectedOption, (newValue) => {
    emit('update:modelValue', newValue ? newValue[props.valueAttribute] : null);
});

watch(() => props.modelValue, (newValue) => {
    if (newValue !== (selectedOption.value ? selectedOption.value[props.valueAttribute] : null)) {
        selectedOption.value = safeOptions.value.find(option => option[props.valueAttribute] === newValue) || null;
        if (props.fetchOptions) {
            searchQuery.value = selectedOption.value ? (selectedOption.value[props.textAttribute] || '') : '';
        }
    }
}, { immediate: true });

// Watch for changes in options (when loaded async)
watch(() => props.options, () => {
    if (!props.fetchOptions && props.modelValue && !selectedOption.value) {
        selectedOption.value = safeOptions.value.find(option => option[props.valueAttribute] === props.modelValue) || null;
    }
}, { deep: true });

watch(safeOptions, () => {
    if (props.modelValue && !selectedOption.value) {
        selectedOption.value = safeOptions.value.find(option => option[props.valueAttribute] === props.modelValue) || null;
    }
}, { deep: true });

onMounted(() => {
    if (props.fetchOptions) {
        document.addEventListener('click', onDocumentClick);
    }
});
onUnmounted(() => {
    if (props.fetchOptions) {
        document.removeEventListener('click', onDocumentClick);
    }
});
</script>

<style scoped>
/* Ensure list item text is always dark (prevent overrides from parent styles) */
:deep(.t-rich-select-dropdown) li,
:deep(.t-rich-select-dropdown) li span,
:deep(.t-rich-select-dropdown) li div {
    color: #111827 !important;
}
</style>

<style>
/* Fallback: if dropdown is rendered outside the component, keep text dark as well */
.t-rich-select-dropdown li,
.t-rich-select-dropdown li span,
.t-rich-select-dropdown li div {
    color: #111827 !important;
}
</style>
